import { JSX } from 'react';
import { Col, Image, Row } from 'antd';
import { ProductKeys, ProductSwitcher } from './productSwitcher';
import { SupportDropdown } from './supportDropdown';
import { UserDropdown } from './userDropdown';
import { Config } from '../../../../config/index';
import { AgencyCode } from '../../../../config/constant';
import { handleUserLogout } from '../../../../utils/index';
import { IOrganizationMember } from '../../../../store/models/redux/index';
import { useActiveMember } from '../../../../hooks/organizationMember/index';
import { useSelector } from 'react-redux';
import { OrganizationSelectors } from '../../../../store/selectors/organization';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import { OrganizationSwitcher } from './organizationSwitcher';
import { Notifications } from './notificationDropdown';

/**
 * [Smart Component] Smart container for the Prospecting Tool's top navigation bar
 */
export function ProspectingToolHeaderContainer(): JSX.Element {
  const activeMember = useActiveMember();
  const logoPath = useOrganizationLogo();

  const onOpenHelpPortal = () => window.open(Config.helpPortalURL);

  const onReportAnIssue = () => window.open(Config.reportBugURL);

  const onLogout = () => handleUserLogout(Config.isB2CEnabled);

  return <ProspectingToolHeader
    member={activeMember}
    onOpenHelpPortal={onOpenHelpPortal}
    onReportAnIssue={onReportAnIssue}
    onLogout={onLogout}
    logoPath={logoPath}
  />;
}

type ProspectingToolHeaderProps = Readonly<{
  /**
   * The current active member
   */
  member: IOrganizationMember | null;
  /**
   * The path to the logo to display in the header
   */
  logoPath: string;
  /**
   * Callback for clicking to open a help portal
   */
  onOpenHelpPortal: () => void;
  /**
   * Callback for clicking to report an issue
   */
  onReportAnIssue: () => void;
  /**
   * Callback for logging out the current user
   */
  onLogout: () => void;
}>;

function ProspectingToolHeader(props: ProspectingToolHeaderProps): JSX.Element {
  const {
    member,
    logoPath,
    onOpenHelpPortal,
    onReportAnIssue,
    onLogout,
  } = props;

  return (
    <Row justify="space-between">
      <Col>
        <Image style={{ maxHeight: 40, width: 'auto' }} src={logoPath} preview={false} />
      </Col>

      <Col style={{ display: 'flex' }}>
        <ProductSwitcher currentProductKey={ProductKeys.teamsAccount} isDark />
        <SupportDropdown handleOpenHelpPortal={onOpenHelpPortal} handleReportAnIssue={onReportAnIssue} isDark />
        <OrganizationSwitcher isDark />
        <Notifications isDark />
        <UserDropdown name={member?.name ?? ''} handleLogout={onLogout} isDark />
      </Col>
    </Row>
  );
}

function useOrganizationLogo(): string {
  // If the current organization has a logo, use it.
  const activeOrganization = useSelector(OrganizationSelectors.getActiveOrganization);
  if (!isNil(activeOrganization) && !isNil(activeOrganization.logoUrl) && !isEmpty(activeOrganization.logoUrl)) {
    // Extensive check added above due to type inference issues with lodash methods
    return activeOrganization?.logoUrl;
  }

  // If an agency is logged in, use their logo.
  if (Config.isAgency) {
    switch (Config.agencyCode) {
      case AgencyCode.cleverly:
        return require('../../../../assets/images/cleverly_white.jpg');
      case AgencyCode.clvwlbl:
        return require('../../../../assets/images/leadgen.png');
      case AgencyCode.prosocial:
      default:
        return require('../../../../assets/images/socially_white.png');
    }
  }

  // Use CoPilot's logo
  return require('../../../../assets/images/copilotai_white.png');
}